import React, { useState, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import { Link, useSearchParams } from 'react-router-dom';
import cn from 'classnames';
import PropertyPurchaseGuide from './components/PropertyPurchaseGuide';

const CATEGORIES = [
  { id: 'all', label: 'Toutes les démarches', icon: 'LayoutGrid' },
  { id: 'immobilier', label: 'Immobilier & Foncier', icon: 'Building2' },
  { id: 'etat-civil', label: 'État Civil', icon: 'FileText' },
  { id: 'entreprise', label: 'Entreprise & Commerce', icon: 'Briefcase' },
  { id: 'fiscalite', label: 'Fiscalité & Impôts', icon: 'Receipt' },
  { id: 'social', label: 'Social & Emploi', icon: 'Users' },
  { id: 'transport', label: 'Transport & Véhicules', icon: 'Car' },
  { id: 'education', label: 'Éducation', icon: 'GraduationCap' },
  { id: 'sante', label: 'Santé', icon: 'Heart' },
];

// (On conserve les mêmes données)
const PROCEDURES = [
  // IMMOBILIER & FONCIER
  {
    id: 1, category: 'immobilier', title: "Obtention d\'un Titre Foncier",
    administration: "Direction Générale des Affaires Foncières (DGAF)",
    ministere: "Ministère de la Construction, du Logement et de l'Urbanisme",
    duree: "3 à 6 mois", cout: "Variable selon la superficie", difficulte: "Complexe",
    description: "Procédure permettant d\'obtenir un titre de propriété officiel sur un terrain ou une parcelle en Côte d\'Ivoire.",
    etapes: [
      "Dépôt de la demande à la Direction Régionale des Affaires Foncières",
      "Enquête de commodo et incommodo (affichage public 30 jours)",
      "Bornage contradictoire par un géomètre agréé",
      "Instruction du dossier par la commission foncière",
      "Établissement du titre foncier et inscription au livre foncier",
    ],
    documents: [
      "Demande manuscrite adressée au Directeur Général",
      "Copie de la CNI ou passeport",
      "Plan de situation du terrain",
      "Acte de vente ou de donation notarié",
      "Quittance de paiement des frais de dossier",
      "Attestation de non-litige délivrée par le chef de village",
    ],
    contact: "Plateau, Abidjan | Tél: +225 27 20 21 00 00",
    lien: "https://www.construction.gouv.ci",
    tag: "Foncier",
  },
  {
    id: 2, category: 'immobilier', title: "Permis de Construire",
    administration: "Direction de l\'Urbanisme et de la Construction",
    ministere: "Ministère de la Construction, du Logement et de l'Urbanisme",
    duree: "2 à 4 mois", cout: "Selon superficie et type de construction", difficulte: "Modéré",
    description: "Autorisation administrative obligatoire avant tout début de travaux de construction ou d\'extension d\'un bâtiment.",
    etapes: [
      "Retrait du formulaire de demande auprès de la mairie ou de la Direction de l\'Urbanisme",
      "Constitution du dossier technique avec un architecte agréé",
      "Dépôt du dossier complet",
      "Instruction par les services techniques (urbanisme, voirie, pompiers)",
      "Délivrance du permis ou notification de refus motivé",
    ],
    documents: [
      "Formulaire de demande rempli et signé",
      "Titre foncier ou attestation de propriété",
      "Plans architecturaux signés par un architecte agréé ONACI",
      "Note de calcul des structures",
      "Étude d'impact environnemental (si nécessaire)",
      "Quittance de paiement des taxes",
    ],
    contact: "Mairie de votre commune | Direction Régionale de la Construction",
    lien: "https://www.construction.gouv.ci",
    tag: "Construction",
  },
  {
    id: 3, category: 'immobilier', title: "Mutation de Titre Foncier",
    administration: "Conservation Foncière",
    ministere: "Ministère de la Construction, du Logement et de l'Urbanisme",
    duree: "1 à 3 mois", cout: "2% à 10% de la valeur du bien", difficulte: "Modéré",
    description: "Transfert officiel de la propriété d'un bien immobilier d'un propriétaire à un autre suite à une vente, donation ou succession.",
    etapes: [
      "Signature de l\'acte de vente chez un notaire",
      "Enregistrement de l\'acte à la Direction des Impôts",
      "Dépôt de la demande de mutation à la Conservation Foncière",
      "Vérification et instruction du dossier",
      "Inscription de la mutation sur le titre foncier",
    ],
    documents: [
      "Acte de vente notarié original",
      "Titre foncier original du vendeur",
      "CNI des deux parties",
      "Quittance de paiement des droits de mutation",
      "Certificat de non-hypothèque",
    ],
    contact: "Conservation Foncière, Plateau, Abidjan",
    lien: "https://www.construction.gouv.ci",
    tag: "Mutation",
  },
  // ÉTAT CIVIL
  {
    id: 4, category: 'etat-civil', title: "Établissement d\'un Acte de Naissance",
    administration: "Mairie / Centre d\'État Civil", ministere: "Ministère de l\'Intérieur et de la Sécurité",
    duree: "1 à 7 jours", cout: "Gratuit (dans les délais légaux)", difficulte: "Simple",
    description: "Déclaration et enregistrement officiel d\'une naissance auprès des services de l\'état civil.",
    etapes: ["Déclaration de naissance dans les 3 mois suivant la naissance", "Présentation des pièces justificatives à la mairie", "Enregistrement dans le registre d\'état civil", "Délivrance du bulletin de naissance"],
    documents: ["Certificat médical de naissance", "CNI ou passeport des parents", "Livret de famille (si existant)", "Acte de mariage des parents (si mariés)"],
    contact: "Mairie de votre commune", lien: "https://www.interieur.gouv.ci", tag: "État Civil",
  },
  {
    id: 5, category: 'etat-civil', title: "Obtention de la Carte Nationale d'Identité (CNI)",
    administration: "Office National de l'État Civil (ONECI)", ministere: "Ministère de l\'Intérieur et de la Sécurité",
    duree: "15 à 30 jours", cout: "5 000 FCFA", difficulte: "Simple",
    description: "Document officiel d'identification des citoyens ivoiriens, obligatoire pour toutes les démarches administratives.",
    etapes: ["Retrait du formulaire de demande à l\'ONECI ou en ligne", "Remplissage et dépôt du dossier au centre ONECI", "Prise d\'empreintes digitales et photo biométrique", "Paiement des frais", "Retrait de la CNI au centre de dépôt"],
    documents: ["Acte de naissance (original + copie)", "Certificat de nationalité ivoirienne", "2 photos d\'identité récentes", "Quittance de paiement"],
    contact: "ONECI - www.oneci.ci | Tél: +225 27 20 22 00 00", lien: "https://www.oneci.ci", tag: "Identité",
  },
  {
    id: 6, category: 'etat-civil', title: "Obtention du Passeport",
    administration: "Direction Générale de la Police Nationale", ministere: "Ministère de l\'Intérieur et de la Sécurité",
    duree: "10 à 21 jours", cout: "25 000 FCFA (5 ans) / 50 000 FCFA (10 ans)", difficulte: "Simple",
    description: "Document de voyage officiel permettant aux citoyens ivoiriens de voyager à l\'étranger.",
    etapes: ["Pré-inscription en ligne sur le portail de la DGPN", "Prise de rendez-vous au centre de délivrance", "Dépôt du dossier et prise biométrique", "Paiement des frais au Trésor Public", "Retrait du passeport"],
    documents: ["CNI en cours de validité", "Acte de naissance", "2 photos biométriques", "Quittance de paiement du Trésor Public", "Ancien passeport (si renouvellement)"],
    contact: "Direction Générale de la Police Nationale, Abidjan", lien: "https://www.interieur.gouv.ci", tag: "Voyage",
  },
  // ENTREPRISE & COMMERCE
  {
    id: 7, category: 'entreprise', title: "Création d'une Entreprise (SARL/SA)",
    administration: "Centre de Formalités des Entreprises (CEPICI)", ministere: "Ministère du Commerce, de l'Industrie et de la Promotion des PME",
    duree: "24 à 72 heures", cout: "À partir de 35 000 FCFA", difficulte: "Modéré",
    description: "Procédure simplifiée de création d'entreprise via le guichet unique du CEPICI permettant d'obtenir tous les documents légaux en une seule démarche.",
    etapes: ["Choix de la forme juridique et du nom de l\'entreprise", "Dépôt du dossier au CEPICI (guichet unique)", "Vérification de la disponibilité du nom commercial", "Enregistrement au RCCM (Registre du Commerce)", "Obtention du numéro de compte contribuable (NCC)", "Délivrance des documents constitutifs"],
    documents: ["Formulaire de demande CEPICI rempli", "Statuts de la société (2 exemplaires notariés)", "CNI des associés et gérants", "Attestation de domiciliation du siège social", "Justificatif de dépôt du capital social", "Casier judiciaire des dirigeants"],
    contact: "CEPICI - Abidjan Plateau | Tél: +225 27 20 25 00 00", lien: "https://www.cepici.gouv.ci", tag: "Entreprise",
  },
  {
    id: 8, category: 'entreprise', title: "Licence d\'Importation/Exportation",
    administration: "Direction du Commerce Extérieur", ministere: "Ministère du Commerce, de l'Industrie et de la Promotion des PME",
    duree: "5 à 15 jours", cout: "Variable", difficulte: "Modéré",
    description: "Autorisation permettant aux entreprises d\'exercer des activités d\'importation ou d\'exportation de marchandises.",
    etapes: ["Inscription au registre des importateurs/exportateurs", "Dépôt de la demande de licence", "Vérification des documents", "Délivrance de la licence"],
    documents: ["Extrait RCCM", "Attestation fiscale", "CNI du responsable", "Formulaire de demande rempli"],
    contact: "Direction du Commerce Extérieur, Plateau", lien: "https://www.commerce.gouv.ci", tag: "Commerce",
  },
  // FISCALITÉ
  {
    id: 9, category: 'fiscalite', title: "Déclaration et Paiement de l\'Impôt",
    administration: "Direction Générale des Impôts (DGI)", ministere: "Ministère de l\'Économie et des Finances",
    duree: "Annuel", cout: "Selon barème", difficulte: "Modéré",
    description: "Obligation annuelle de déclaration des revenus et paiement de l\'impôt.",
    etapes: ["Collecte des justificatifs", "Remplissage de la déclaration", "Calcul de l\'impôt", "Paiement", "Conservation de la quittance"],
    documents: ["Bulletins de salaire", "Relevés bancaires", "Justificatifs de charges", "Numéro de Compte Contribuable"],
    contact: "DGI - www.dgi.gouv.ci", lien: "https://www.dgi.gouv.ci", tag: "Impôts",
  },
  {
    id: 10, category: 'fiscalite', title: "Obtention d\'une Attestation Fiscale",
    administration: "Direction Générale des Impôts (DGI)", ministere: "Ministère de l\'Économie et des Finances",
    duree: "24 à 72 heures", cout: "Gratuit", difficulte: "Simple",
    description: "Document attestant que le contribuable est en règle vis-à-vis de ses obligations fiscales.",
    etapes: ["Connexion sur e-impôts.ci", "Vérification de la situation", "Demande d\'attestation", "Téléchargement"],
    documents: ["Numéro de Compte Contribuable (NCC)", "CNI ou passeport", "Être à jour de ses obligations"],
    contact: "DGI - www.e-impots.ci", lien: "https://www.dgi.gouv.ci", tag: "Attestation",
  },
  // SOCIAL & EMPLOI
  {
    id: 11, category: 'social', title: "Immatriculation à la CNPS",
    administration: "Caisse Nationale de Prévoyance Sociale (CNPS)", ministere: "Ministère de l\'Emploi et de la Protection Sociale",
    duree: "5 à 10 jours", cout: "Gratuit", difficulte: "Simple",
    description: "Enregistrement obligatoire à la sécurité sociale pour les travailleurs salariés et les employeurs.",
    etapes: ["Retrait du formulaire", "Remplissage et dépôt", "Vérification", "Attribution du numéro", "Délivrance de la carte"],
    documents: ["Acte de naissance", "CNI ou passeport", "Contrat de travail", "2 photos d\'identité"],
    contact: "CNPS - www.cnps.ci", lien: "https://www.cnps.ci", tag: "Sécurité Sociale",
  },
  {
    id: 12, category: 'social', title: "Demande d\'Allocation Chômage",
    administration: "Agence Emploi Jeunes (AEJ)", ministere: "Ministère de l\'Emploi et de la Protection Sociale",
    duree: "1 à 3 mois", cout: "Gratuit", difficulte: "Modéré",
    description: "Procédure permettant aux travailleurs ayant perdu leur emploi de bénéficier d\'une aide financière temporaire.",
    etapes: ["Inscription à l\'AEJ", "Dépôt du dossier", "Entretien", "Instruction", "Versement si éligible"],
    documents: ["Lettre de licenciement", "Relevé de carrière CNPS", "CNI", "RIB", "CV"],
    contact: "AEJ - www.agenceemploijeunes.ci", lien: "https://www.emploi.gouv.ci", tag: "Emploi",
  },
  // TRANSPORT
  {
    id: 13, category: 'transport', title: "Obtention du Permis de Conduire",
    administration: "Direction des Transports Terrestres (DTT)", ministere: "Ministère des Transports",
    duree: "1 à 3 mois", cout: "Dès 150 000 FCFA", difficulte: "Modéré",
    description: "Autorisation officielle de conduire un véhicule à moteur sur les voies publiques.",
    etapes: ["Inscription auto-école", "Formation théorique", "Formation pratique", "Examen théorique", "Examen pratique", "Délivrance"],
    documents: ["CNI ou passeport", "Certificat médical", "2 photos", "Quittance de paiement"],
    contact: "DTT - Abidjan", lien: "https://www.transports.gouv.ci", tag: "Conduite",
  },
  {
    id: 14, category: 'transport', title: "Immatriculation d\'un Véhicule",
    administration: "Direction des Transports Terrestres (DTT)", ministere: "Ministère des Transports",
    duree: "3 à 7 jours", cout: "Variable", difficulte: "Simple",
    description: "Enregistrement officiel d\'un véhicule pour obtenir une plaque d\'immatriculation.",
    etapes: ["Dépôt du dossier", "Vérification technique", "Paiement des taxes", "Attribution du numéro", "Délivrance de la carte grise"],
    documents: ["Facture d\'achat", "Quittance de dédouanement", "CNI du propriétaire", "Certificat technique", "Quittance des taxes"],
    contact: "DTT - Abidjan", lien: "https://www.transports.gouv.ci", tag: "Véhicule",
  },
  // ÉDUCATION
  {
    id: 15, category: 'education', title: "Inscription dans une École Publique",
    administration: "Direction Régionale de l\'Éducation Nationale", ministere: "Ministère de l\'Éducation Nationale",
    duree: "Variable", cout: "Gratuit", difficulte: "Simple",
    description: "Procédure d\'inscription d\'un enfant dans une école publique.",
    etapes: ["Renseignement", "Retrait du formulaire", "Dépôt du dossier", "Validation"],
    documents: ["Acte de naissance", "Carnet de vaccination", "Certificat de scolarité (si transfert)", "Photos", "CNI du parent"],
    contact: "Direction Régionale de votre région", lien: "https://www.men.gouv.ci", tag: "Scolarité",
  },
  // SANTÉ
  {
    id: 16, category: 'sante', title: "Carte d'Assurance Maladie Universelle (CMU)",
    administration: "Caisse Nationale d'Assurance Maladie (CNAM)", ministere: "Ministère de la Santé",
    duree: "7 à 15 jours", cout: "1 000 FCFA/mois", difficulte: "Simple",
    description: "Couverture maladie universelle pour accéder aux soins de santé à moindre coût.",
    etapes: ["Inscription en ligne ou en centre", "Prise biométrique", "Paiement cotisation", "Délivrance"],
    documents: ["CNI ou acte de naissance", "Photo d\'identité", "Justificatif de domicile", "Quittance"],
    contact: "CNAM - www.cmu.ci", lien: "https://www.cmu.ci", tag: "Santé",
  }
];

// Configuration adaptée au mode sombre absolu
const DIFFICULTE_CONFIG = {
  'Simple': { color: 'text-emerald-400 bg-emerald-500/10', dot: 'bg-emerald-400' },
  'Modéré': { color: 'text-brand-gold bg-brand-gold/10', dot: 'bg-brand-gold' },
  'Complexe': { color: 'text-red-400 bg-red-500/10', dot: 'bg-red-400' },
};

const ProcedureCard = ({ procedure, onClick, isSelected }) => {
  const diff = DIFFICULTE_CONFIG?.[procedure?.difficulte] || DIFFICULTE_CONFIG?.['Modéré'];
  return (
    <button
      onClick={() => onClick(procedure)}
      className={cn(
        "w-full text-left rounded-2xl p-5 transition-all duration-300 group border-none",
        isSelected
          ? "bg-brand-gold/10 ring-1 ring-brand-gold/50 shadow-[0_0_20px_rgba(212,175,55,0.1)]"
          : "bg-[#111111] hover:bg-[#1A1A1A] hover:-translate-y-1"
      )}
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <h3 className={cn(
          "font-heading font-bold text-base leading-snug transition-colors",
          isSelected ? "text-brand-gold" : "text-white group-hover:text-brand-gold"
        )}>
          {procedure?.title}
        </h3>
        <span className={cn("flex-shrink-0 text-[10px] font-bold px-2.5 py-1 rounded-full flex items-center gap-1.5 uppercase tracking-widest", diff?.color)}>
          <span className={cn("w-1.5 h-1.5 rounded-full shadow-[0_0_5px_currentColor]", diff?.dot)} />
          {procedure?.difficulte}
        </span>
      </div>
      <p className="text-sm text-white/50 mb-4 line-clamp-2 font-light leading-relaxed">{procedure?.description}</p>
      <div className="flex items-center gap-4 text-xs font-medium text-white/40">
        <span className="flex items-center gap-1.5">
          <Icon name="Clock" size={14} className="text-brand-gold" />
          {procedure?.duree}
        </span>
        <span className="flex items-center gap-1.5">
          <Icon name="Banknote" size={14} className="text-brand-gold" />
          {procedure?.cout}
        </span>
      </div>
      <div className="mt-4 pt-4 border-t border-white/5">
        <p className="text-[11px] text-white/30 uppercase tracking-widest font-bold truncate">{procedure?.administration}</p>
      </div>
    </button>
  );
};

const ProcedureDetail = ({ procedure, onClose }) => {
  if (!procedure) return null;
  const diff = DIFFICULTE_CONFIG?.[procedure?.difficulte] || DIFFICULTE_CONFIG?.['Modéré'];

  return (
    <div className="bg-[#111111] border-none rounded-3xl overflow-hidden sticky top-24 shadow-2xl">
      {/* Header */}
      <div className="relative bg-gradient-to-br from-brand-gold/10 to-transparent p-8 border-b border-white/5">
        <div className="flex items-start justify-between gap-3 mb-4">
          <span className="text-[10px] font-bold text-brand-gold uppercase tracking-widest bg-brand-gold/10 px-3 py-1.5 rounded-full border border-brand-gold/20">
            {procedure?.tag}
          </span>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center text-white/50 hover:text-white transition-colors border-none lg:hidden">
            <Icon name="X" size={14} strokeWidth={2.5} />
          </button>
        </div>
        <h2 className="font-heading font-bold text-white text-2xl leading-snug mb-2">{procedure?.title}</h2>
        <p className="text-sm font-medium text-white/60">{procedure?.administration}</p>
        <p className="text-xs text-white/30 mt-1 uppercase tracking-widest">{procedure?.ministere}</p>
      </div>
      
      <div className="p-8 space-y-8 max-h-[calc(100vh-280px)] overflow-y-auto overscroll-contain">
        
        {/* Stats row */}
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-white/5 rounded-2xl p-4 text-center border-none">
            <Icon name="Clock" size={18} className="text-brand-gold mx-auto mb-2" />
            <p className="text-[10px] text-white/40 uppercase tracking-widest font-bold">Durée</p>
            <p className="text-sm font-bold text-white mt-1">{procedure?.duree}</p>
          </div>
          <div className="bg-white/5 rounded-2xl p-4 text-center border-none">
            <Icon name="Banknote" size={18} className="text-brand-gold mx-auto mb-2" />
            <p className="text-[10px] text-white/40 uppercase tracking-widest font-bold">Coût</p>
            <p className="text-sm font-bold text-white mt-1">{procedure?.cout}</p>
          </div>
          <div className={cn("rounded-2xl p-4 text-center border-none", diff?.color)}>
            <Icon name="BarChart2" size={18} className="mx-auto mb-2" />
            <p className="text-[10px] uppercase tracking-widest font-bold opacity-70">Difficulté</p>
            <p className="text-sm font-bold mt-1">{procedure?.difficulte}</p>
          </div>
        </div>

        {/* Description */}
        <div>
          <h4 className="text-[10px] font-bold text-white/40 uppercase tracking-widest mb-3">Description</h4>
          <p className="text-base text-white/70 font-light leading-relaxed">{procedure?.description}</p>
        </div>

        {/* Étapes */}
        <div>
          <h4 className="text-[10px] font-bold text-white/40 uppercase tracking-widest mb-4">Étapes de la procédure</h4>
          <div className="space-y-4">
            {procedure?.etapes?.map((etape, i) => (
              <div key={i} className="flex gap-4 group">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-brand-gold/10 text-brand-gold text-xs font-bold flex items-center justify-center border border-brand-gold/20 shadow-[0_0_10px_rgba(212,175,55,0.0)] group-hover:shadow-[0_0_10px_rgba(212,175,55,0.3)] transition-shadow">
                  {i + 1}
                </div>
                <p className="text-sm text-white/70 font-light leading-relaxed pt-1.5">{etape}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Documents */}
        <div>
          <h4 className="text-[10px] font-bold text-white/40 uppercase tracking-widest mb-4">Documents requis</h4>
          <div className="space-y-3 bg-white/5 p-5 rounded-2xl">
            {procedure?.documents?.map((doc, i) => (
              <div key={i} className="flex items-start gap-3">
                <Icon name="CheckCircle2" size={16} className="text-brand-gold flex-shrink-0 mt-0.5" />
                <p className="text-sm text-white/80 font-light">{doc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Contact */}
        <div className="bg-brand-gold/10 border border-brand-gold/20 rounded-2xl p-6">
          <h4 className="text-[10px] font-bold text-brand-gold uppercase tracking-widest mb-3">Contact & Localisation</h4>
          <p className="text-sm text-white/90 font-medium mb-4">{procedure?.contact}</p>
          <a
            href={procedure?.lien}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 w-full bg-brand-gold text-black text-xs font-bold uppercase tracking-widest py-3 rounded-xl hover:bg-yellow-400 transition-colors border-none"
          >
            <Icon name="ExternalLink" size={14} />
            Site officiel de l'administration
          </a>
        </div>
      </div>
    </div>
  );
};

const DemarchesAdministratives = () => {
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedProcedure, setSelectedProcedure] = useState(null);
  const [searchParams] = useSearchParams();

  const propertyContext = searchParams?.get('property') || null;
  const transactionType = searchParams?.get('type') || null;
  const contextLabel = propertyContext
    ? `${transactionType === 'achat' ? 'Achat' : 'Transaction'} · ${propertyContext}`
    : null;

  const [showPurchaseGuide, setShowPurchaseGuide] = useState(true);

  const filtered = useMemo(() => {
    return PROCEDURES?.filter(p => {
      const matchCat = activeCategory === 'all' || p?.category === activeCategory;
      const q = searchQuery?.toLowerCase();
      const matchSearch = !q || p?.title?.toLowerCase()?.includes(q) || p?.administration?.toLowerCase()?.includes(q) || p?.description?.toLowerCase()?.includes(q) || p?.tag?.toLowerCase()?.includes(q);
      return matchCat && matchSearch;
    });
  }, [activeCategory, searchQuery]);

  const handleSelect = (procedure) => {
    setSelectedProcedure(prev => prev?.id === procedure?.id ? null : procedure);
  };

  return (
    <>
      <Helmet>
        <title>Démarches Administratives - Côte d'Ivoire | Kapital States</title>
        <meta name="description" content="Retrouvez toutes les procédures et démarches administratives des administrations publiques de Côte d'Ivoire : immobilier, état civil, entreprise, fiscalité, transport et plus." />
      </Helmet>
      
      {/* Fond Noir Absolu Global */}
      <div className="min-h-screen bg-[#050505]">
        <Header />

        {/* Hero Banner (Noir profond + halo doré) */}
        <div className="relative border-b border-white/5 bg-[#050505] overflow-hidden">
          <div className="absolute top-0 right-1/4 w-[600px] h-[600px] bg-brand-gold/5 rounded-full blur-[150px] pointer-events-none" />
          
          <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-16 md:py-24">
            <div className="flex items-center gap-3 text-[10px] font-bold uppercase tracking-widest text-white/30 mb-8">
              <Link to="/homepage" className="hover:text-white transition-colors">Accueil</Link>
              <Icon name="ChevronRight" size={10} />
              <span className="text-brand-gold">Démarches Administratives</span>
            </div>
            
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-10">
              <div className="max-w-2xl">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 rounded-xl bg-brand-gold/10 flex items-center justify-center border border-brand-gold/20 shadow-[0_0_15px_rgba(212,175,55,0.15)]">
                    <Icon name="Landmark" size={24} className="text-brand-gold" />
                  </div>
                  <span className="text-[11px] font-bold text-brand-gold uppercase tracking-widest">Administrations Publiques</span>
                </div>
                <h1 className="font-heading font-bold text-white text-4xl md:text-5xl lg:text-6xl leading-[1.1] mb-6 tracking-tight">
                  Démarches Administratives<br />
                  <span className="text-brand-gold text-3xl md:text-4xl lg:text-5xl">Côte d'Ivoire</span>
                </h1>
                <p className="text-white/50 font-light text-lg md:text-xl leading-relaxed">
                  Toutes les procédures officielles des administrations publiques ivoiriennes — étapes, documents requis, délais et contacts.
                </p>
              </div>
              
              <div className="flex items-center gap-6 bg-[#111111] p-6 rounded-2xl border border-white/5">
                <div className="text-center px-2">
                  <p className="text-4xl font-heading font-bold text-white mb-1">{PROCEDURES?.length}</p>
                  <p className="text-[10px] font-bold text-brand-gold uppercase tracking-widest">Procédures</p>
                </div>
                <div className="w-px h-12 bg-white/10" />
                <div className="text-center px-2">
                  <p className="text-4xl font-heading font-bold text-white mb-1">{CATEGORIES?.length - 1}</p>
                  <p className="text-[10px] font-bold text-brand-gold uppercase tracking-widest">Domaines</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-12">

          {/* Guide contextuel */}
          <div className="mb-12">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <Icon name="Home" size={18} className="text-brand-gold" />
                <h2 className="text-xs font-bold text-white uppercase tracking-widest">Guide d'Achat Immobilier</h2>
                <span className="text-[9px] font-bold bg-brand-gold/20 text-brand-gold px-2.5 py-1 rounded-full uppercase tracking-wider">Contextuel</span>
              </div>
              <button
                onClick={() => setShowPurchaseGuide(p => !p)}
                className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-white/30 hover:text-white transition-colors"
              >
                <Icon name={showPurchaseGuide ? 'EyeOff' : 'Eye'} size={14} />
                {showPurchaseGuide ? 'Masquer' : 'Afficher'}
              </button>
            </div>
            {showPurchaseGuide && (
              <PropertyPurchaseGuide propertyContext={contextLabel} />
            )}
          </div>

          {/* Barre de Recherche (Glassmorphism) */}
          <div className="relative mb-8">
            <Icon name="Search" size={18} className="absolute left-5 top-1/2 -translate-y-1/2 text-white/40" />
            <input
              type="text"
              placeholder="Rechercher une démarche, une administration..."
              value={searchQuery}
              onChange={e => setSearchQuery(e?.target?.value)}
              className="w-full pl-14 pr-14 py-5 bg-[#111111] border-none rounded-2xl text-base text-white placeholder:text-white/30 focus:outline-none focus:ring-1 focus:ring-brand-gold transition-all shadow-inner"
            />
            {searchQuery && (
              <button onClick={() => setSearchQuery('')} className="absolute right-5 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors border-none">
                <Icon name="X" size={14} className="text-white/60" />
              </button>
            )}
          </div>

          {/* Onglets (Tabs) */}
          <div className="flex gap-3 overflow-x-auto pb-4 mb-8 scrollbar-hide">
            {CATEGORIES?.map(cat => (
              <button
                key={cat?.id}
                onClick={() => { setActiveCategory(cat?.id); setSelectedProcedure(null); }}
                className={cn(
                  "flex-shrink-0 flex items-center gap-2 px-5 py-3 rounded-xl text-xs font-bold uppercase tracking-wider transition-all duration-300 border-none",
                  activeCategory === cat?.id
                    ? "bg-brand-gold text-black shadow-[0_0_15px_rgba(212,175,55,0.3)]"
                    : "bg-[#111111] text-white/50 hover:bg-[#1A1A1A] hover:text-white"
                )}
              >
                <Icon name={cat?.icon} size={14} strokeWidth={2.5} />
                {cat?.label}
              </button>
            ))}
          </div>

          {/* Compteur de résultats */}
          <div className="flex items-center justify-between mb-6">
            <p className="text-sm font-light text-white/50">
              <span className="font-bold text-white">{filtered?.length}</span> procédure{filtered?.length !== 1 ? 's' : ''} trouvée{filtered?.length !== 1 ? 's' : ''}
            </p>
          </div>

          {/* Grille principale */}
          <div className={cn("grid gap-8 items-start", selectedProcedure ? "lg:grid-cols-[1fr_480px]" : "grid-cols-1")}>
            
            {/* Liste des Cartes */}
            <div>
              {filtered?.length === 0 ? (
                <div className="text-center py-20 bg-[#111111] rounded-3xl border-none">
                  <div className="w-16 h-16 rounded-2xl bg-white/5 flex items-center justify-center mx-auto mb-6">
                    <Icon name="SearchX" size={28} className="text-brand-gold" />
                  </div>
                  <p className="font-heading font-bold text-white text-xl mb-2">Aucune procédure trouvée</p>
                  <p className="text-white/50 font-light">Essayez un autre terme de recherche ou une autre catégorie.</p>
                </div>
              ) : (
                <div className={cn("grid gap-5", selectedProcedure ? "grid-cols-1 sm:grid-cols-2" : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3")}>
                  {filtered?.map(p => (
                    <ProcedureCard
                      key={p?.id}
                      procedure={p}
                      onClick={handleSelect}
                      isSelected={selectedProcedure?.id === p?.id}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Panneau de Détails (s'affiche uniquement si on clique) */}
            {selectedProcedure && (
              <ProcedureDetail procedure={selectedProcedure} onClose={() => setSelectedProcedure(null)} />
            )}
          </div>

          {/* Bannière d'Avertissement */}
          <div className="mt-16 bg-brand-gold/10 border border-brand-gold/20 rounded-2xl p-6 flex items-start gap-5">
            <Icon name="Info" size={24} className="text-brand-gold flex-shrink-0" />
            <div>
              <p className="text-xs font-bold uppercase tracking-widest text-brand-gold mb-2">Note d'Information Légale</p>
              <p className="text-sm font-light text-white/70 leading-relaxed">
                Les informations présentées sont fournies à titre indicatif pour faciliter vos démarches en Côte d'Ivoire. 
                Les lois, délais et coûts administratifs peuvent évoluer. Nous vous recommandons de contacter directement l'administration concernée pour valider votre dossier.
              </p>
            </div>
          </div>

        </div>
      </div>
    </>
  );
};

export default DemarchesAdministratives;